import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCircle, Warning, X, EnvelopeSimple } from "@phosphor-icons/react";
import api from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import { toast } from "sonner";

function fmtDelta(gg) {
  if (gg < 0) return `Scaduta ${Math.abs(gg)} gg fa`;
  if (gg === 0) return "Oggi";
  if (gg === 1) return "Domani";
  return `Tra ${gg} giorni`;
}

export default function NotifichePopover() {
  const [data, setData] = useState({ items: [], count: 0, urgent_count: 0 });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (document.visibilityState === "hidden") return;
    try {
      const r = await api.get("/notifiche");
      setData(r.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 60000); // refresh ogni 60s
    return () => clearInterval(iv);
  }, [load]);

  const dismiss = async (key) => {
    try {
      await api.post("/notifiche/dismiss", { key });
      // Ricarica per mantenere coerente count + urgent_count
      load();
    } catch { toast.error("Errore"); }
  };

  const dismissAll = async () => {
    setLoading(true);
    try {
      await api.post("/notifiche/dismiss-all");
      setData({ items: [], count: 0, urgent_count: 0 });
      toast.success("Notifiche archiviate");
    } catch { toast.error("Errore"); }
    setLoading(false);
  };

  const badgeCount = data.count;
  const hasUrgent = data.urgent_count > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notifiche-bell"
          className="relative p-2 rounded-md hover:bg-slate-100 transition-colors flex items-center justify-center"
          aria-label="Notifiche scadenze"
        >
          <Bell size={18} weight={badgeCount > 0 ? "fill" : "regular"} className={badgeCount > 0 ? (hasUrgent ? "text-red-600" : "text-amber-600") : "text-slate-500"} />
          {badgeCount > 0 && (
            <span
              className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center ${hasUrgent ? "bg-red-600" : "bg-amber-600"}`}
              data-testid="notifiche-badge"
            >
              {badgeCount > 9 ? "9+" : badgeCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" side="right">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div>
            <div className="font-heading font-semibold text-sm text-slate-900">Notifiche scadenze</div>
            <div className="text-[10px] font-mono uppercase text-slate-500">Ultimi 7 giorni + scadute</div>
          </div>
          {data.items.length > 0 && (
            <Button variant="ghost" size="sm" onClick={dismissAll} disabled={loading} className="text-xs h-7" data-testid="notifiche-dismiss-all">
              <CheckCircle size={12} className="mr-1" /> Archivia tutte
            </Button>
          )}
        </div>
        <div className="max-h-[400px] overflow-y-auto">
          {data.items.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400" data-testid="notifiche-empty">
              <Bell size={28} className="mx-auto mb-2 text-slate-300" />
              Nessuna scadenza imminente
            </div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="notifiche-list">
              {data.items.map((n) => {
                const isUrgent = n.urgency === "scaduta" || n.urgency === "oggi";
                return (
                  <li key={n.key} className={`p-3 hover:bg-slate-50 ${isUrgent ? "bg-red-50/40" : ""}`} data-testid={`notifica-${n.key}`}>
                    <div className="flex items-start gap-2">
                      {n.tipo === "posta" ? <EnvelopeSimple size={16} weight="fill" className={`flex-shrink-0 mt-0.5 ${isUrgent ? "text-amber-600" : "text-emerald-600"}`} /> : <Warning size={16} weight="fill" className={`flex-shrink-0 mt-0.5 ${isUrgent ? "text-red-600" : "text-amber-500"}`} />}
                      <div className="flex-1 min-w-0">
                        <Link
                          to={n.link || `/pratiche/${n.pratica_id}#scadenze`}
                          onClick={() => setOpen(false)}
                          className="block group"
                          data-testid={`notifica-link-${n.key}`}
                        >
                          <div className="text-sm font-semibold text-slate-900 group-hover:text-blue-700 truncate">{n.nome}</div>
                          <div className="text-[11px] text-slate-500 truncate">{n.pratica_titolo}</div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono uppercase ${isUrgent ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                              {fmtDelta(n.giorni_rimanenti)}
                            </span>
                            <span className="text-[10px] text-slate-400 font-mono">{new Date(n.data).toLocaleDateString("it-IT")}</span>
                            {n.tipo === "custom" && <span className="text-[10px] text-blue-600 font-mono uppercase">custom</span>}
                          </div>
                        </Link>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); dismiss(n.key); }}
                        className="text-slate-300 hover:text-slate-700 p-0.5 flex-shrink-0"
                        title="Archivia"
                        data-testid={`notifica-dismiss-${n.key}`}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
